/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
  ConfigureListFilterResponse,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';

/**
 * Fetch all devices from device hub
 */
export const fetchAllDevices = createAsyncThunk(
  'deviceHub/fetchAllDevices',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchAllDevices(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch devices',
          status: true,
        },
      });
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Refresh devices without toggling loading state
 */
export const refreshDevices = createAsyncThunk(
  'deviceHub/refreshDevices',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchAllDevices(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to refresh devices',
          status: true,
        },
      });
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * Fetch device statistics
 */
export const fetchDeviceDetails = createAsyncThunk(
  'deviceHub/fetchDeviceDetails',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchDeviceDetails();
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch device stats',
          status: true,
        },
      });
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Fetch charger connector status details
 */
export const fetchConnectorStatus = createAsyncThunk(
  'deviceHub/fetchConnectorStatus',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.connectorStatus();
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch connector status',
          status: true,
        },
      });
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Fetch device hub filters
 */
export const fetchDeviceHubFilters = createAsyncThunk<CommonFilterProperties[], any>(
  'deviceHub/fetchDeviceHubFilters',
  async (query, { dispatch }) => {
    try {
      const response = await deviceHubApi.deviceFilter(query);
      console.log('response', response);
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
        response?.data?.filters,
      );
      console.log('updatedResponse', updatedResponse);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Device Hub slice
 * @param {any} data - The data
 */
/**
 * Utility to parse connector status counts in a robust way
 */
const parseConnectorStats = (data: any) => {
  const stats = {
    total: 0,
    available: 0,
    charging: 0,
    preparing: 0,
    finishing: 0,
    unknown: 0,
    unavailable: 0,
    faulted: 0,
    suspendedEv: 0,
    suspendedEvse: 0,
    onlineDevices: 0,
    reservations: 0,
  };

  if (!data) return stats;

  // Handle case: array response (list of statuses)
  let resultsArray = null;
  if (Array.isArray(data)) {
    resultsArray = data;
  } else if (data && Array.isArray(data.finalResult)) {
    resultsArray = data.finalResult;
  } else if (data && Array.isArray(data.results)) {
    resultsArray = data.results;
  }

  if (resultsArray) {
    resultsArray.forEach((item: any) => {
      if (item && typeof item === 'object') {
        const status = (item.status || item.connectorStatus || '').toLowerCase();
        const count = Number(item.count || item.value || 0);

        if (status === 'available') stats.available = count;
        else if (status === 'charging') stats.charging = count;
        else if (status === 'preparing') stats.preparing = count;
        else if (status === 'finishing') stats.finishing = count;
        else if (status === 'unavailable') stats.unavailable = count;
        else if (status === 'faulted') stats.faulted = count;
        else if (status === 'suspendedev') stats.suspendedEv = count;
        else if (status === 'suspendedevse') stats.suspendedEvse = count;
        else if (status === 'reservations') stats.reservations = count;
        else if (status === 'unknown') stats.unknown = count;
      }
    });

    // Compute total if not directly provided
    stats.total = Number(
      data.total ||
        data.totalConnectors ||
        data.totalCount ||
        stats.available +
          stats.charging +
          stats.preparing +
          stats.finishing +
          stats.unavailable +
          stats.faulted +
          stats.suspendedEv +
          stats.suspendedEvse +
          stats.reservations +
          stats.unknown,
    );
  } else if (typeof data === 'object') {
    // Handle case: direct object with fields (checking multiple casing formats)
    stats.total = Number(data.total ?? data.totalConnectors ?? data.totalCount ?? 0);
    stats.available = Number(data.available ?? data.Available ?? 0);
    stats.charging = Number(data.charging ?? data.Charging ?? 0);
    stats.preparing = Number(data.preparing ?? data.Preparing ?? 0);
    stats.finishing = Number(data.finishing ?? data.Finishing ?? 0);
    stats.unknown = Number(data.unknown ?? data.Unknown ?? 0);
    stats.unavailable = Number(data.unavailable ?? data.Unavailable ?? 0);
    stats.faulted = Number(data.faulted ?? data.Faulted ?? 0);
    stats.suspendedEv = Number(data.suspendedEv ?? data.suspendedEV ?? data.SuspendedEV ?? 0);
    stats.suspendedEvse = Number(
      data.suspendedEvse ?? data.suspendedEVSE ?? data.SuspendedEVSE ?? 0,
    );
    stats.reservations = Number(data.reservations ?? data.Reservations ?? 0);
  }

  // Set online devices if provided
  stats.onlineDevices = Number(
    data.onlineDevices ??
      data.onlineDeviceCount ??
      data.onlineDevicesCount ??
      data.onlineCount ??
      0,
  );

  return stats;
};

const initialState = {
  devices: [] as any[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  page: 1,
  limit: 20,
  deviceStats: {
    totalDevices: 0,
    acDeviceCount: 0,
    dcDeviceCount: 0,
    cpoAssociatedCount: 0,
    nonAssociatedCount: 0,
    hybridDeviceCount: 0,
    blockedDeviceCount: 0,
    onlineDeviceCount: 0,
  },
  connectorStats: {
    total: 0,
    available: 0,
    charging: 0,
    preparing: 0,
    finishing: 0,
    unknown: 0,
    unavailable: 0,
    faulted: 0,
    suspendedEv: 0,
    suspendedEvse: 0,
    onlineDevices: 0,
    reservations: 0,
  },
  filters: availableFilters,
  isFilterUpdated: false,
  filterListLoading: false,
  sortBy: 'desc' as 'asc' | 'desc' | null,
  sortField: 'createdAt' as string | null,
};

const deviceHubSlice = createSlice({
  name: 'deviceHub',
  initialState,
  reducers: {
    /**
     * Set current page
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setPage: (state, action) => {
      state.page = action.payload;
    },
    /**
     * Set limit
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    /**
     * Update filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateDeviceHubFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Disable filter applied flag
     * @param {any} state The current slice state.
     */
    disableDeviceHubFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Handle asynchronous thunk actions
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDevices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchAllDevices.rejected, (state) => {
        state.loading = false;
      })
      .addCase(refreshDevices.fulfilled, (state, action) => {
        // Update state without toggling loading to avoid UI flicker
        state.devices = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchDeviceDetails.fulfilled, (state, action) => {
        state.deviceStats = action.payload || state.deviceStats;
      })
      .addCase(fetchConnectorStatus.fulfilled, (state, action) => {
        state.connectorStats = parseConnectorStats(action.payload);
      })
      .addCase(fetchDeviceHubFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchDeviceHubFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchDeviceHubFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setPage,
  setLimit,
  updateDeviceHubFilters,
  disableDeviceHubFilterApplied,
  setSortData,
} = deviceHubSlice.actions;

export default deviceHubSlice.reducer;
